import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Paginated, Tag } from "@/lib/types";

interface TagsQuery {
  search?: string;
  page?: number;
}

/** GET /api/v1/tags/ — alphabetical, paginated by DRF default (20). */
export function useTags(q: TagsQuery = {}) {
  return useQuery<Paginated<Tag>>({
    queryKey: ["tags", q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q.search) params.set("search", q.search);
      if (q.page)   params.set("page",   String(q.page));
      const { data } = await api.get<Paginated<Tag>>(
        `/tags/?${params.toString()}`,
      );
      return data;
    },
  });
}

/** POST /api/v1/tags/. Director+ only — API enforces. */
export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post<Tag>("/tags/", { name });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

/** PATCH /api/v1/tags/<id>/. */
export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await api.patch<Tag>(`/tags/${id}/`, { name });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

/** DELETE /api/v1/tags/<id>/. */
export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/tags/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}
